import { API } from "aws-amplify";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import RegionList, { getOptimalRegion } from "./RegionList";
import FaqPanel from "./FaqPanel";
import { getToken } from "../utils";
import { isFreeOrDisposableEmail } from "../utils/blockedEmailDomains";
import "./index.css";
import "./CreateAccountForm.css";
import { Elements } from '@stripe/react-stripe-js';
import { getStripePromise, warmStripe } from '../stripe/stripeClient';
import StripeCheckoutForm from './StripeCheckoutForm';

const CreateAccountForm = ({ loggedInUser, isAdmin, accountList, fetchAccountStatusList, pinnedNamePrefix = "" }) => {
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [checkoutAmount, setCheckoutAmount] = useState(null);
  const [checkoutBreakdown, setCheckoutBreakdown] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const [paymentRequestId, setPaymentRequestId] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const [checkoutMode, setCheckoutMode] = useState('elements');
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    // Dynamically load the Stripe Publishable Key securely from Secrets Manager via the backend API
    const fetchStripeConfig = async () => {
      try {
        const response = await API.get("accountAppBackend", "/config");
        if (response.publishableKey) {
          setStripePromise(getStripePromise(response.publishableKey));
          warmStripe(response.publishableKey);
          setCheckoutMode(response.checkoutMode || 'elements');
        } else {
          console.error("Missing publishableKey in API config");
        }
      } catch (err) {
        console.error("Failed to load Stripe configuration:", err);
      }
    };
    fetchStripeConfig();
  }, []);


  
  const FAQ_DATA = {
    region: {
      title: "Region Selection",
      items: [
        { q: "What is an AWS Region?", a: "A physical location where your computing resources are hosted. Selecting the closest region drastically reduces latency." },
        { q: "Can I change my region later?", a: "No, this is a permanent choice for this specific environment. You would need to create a new environment to switch regions." }
      ]
    },
    subdomain: {
      title: "Subdomain Prefix",
      items: [
        { q: "What is a subdomain prefix?", a: "This string is prepended to the system URL. For example, if you enter 'acme', your portal will be 'acme.auth.company.com'." },
        { q: "Can I use special characters?", a: "No, only alphanumeric characters and hyphens are allowed. It must not start or end with a hyphen." }
      ]
    },
    compliance: {
      title: "Data Compliance Controls",
      items: [
        { q: "What is Object Lock?", a: "Object Lock prevents objects in your storage buckets from being deleted or overwritten for a fixed amount of time or indefinitely." },
        { q: "What is Governance Mode?", a: "In Governance mode, users can't overwrite or delete an object version or alter its lock settings unless they have special permissions." }
      ]
    },
    fleet: {
      title: "Fleet Type & Session",
      items: [
        { q: "Always-On vs On-Demand?", a: "Always-On means instances run continuously for instant access but cost more. On-Demand spins up instances as users connect, saving costs but adding a 1-2 minute start time." },
        { q: "Single vs Multi Session?", a: "Single session devotes an entire instance to one user. Multi session allows multiple users to share a single powerful instance, reducing overall costs." },
        { q: "About the Usage Deposit (Escrow)", a: "Heads up: the usage deposit shown at checkout is an ESTIMATE calculated from the maximum number of users and the workstation profile you choose. Your real usage may be higher or lower, so the amount actually consumed from this deposit can differ from the estimate. Any unused portion remains on file against future usage; if your usage exceeds the estimate you may be asked to top it up." }
      ]
    }
  };

  const [companyName, setCompanyName] = useState(""); //RemotetekTest
  const [isAccountRequested, setIsAccountRequested] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  // Client-side rate limit (OWASP A04 — defence in depth on top of backend 5/min/user).
  // Synchronous refs beat React state for double-click protection because they update
  // before the next render cycle, blocking rapid-fire clicks immediately.
  const submitInFlightRef = useRef(false);
  const lastSubmitAtRef = useRef(0);
  const MIN_SUBMIT_INTERVAL_MS = 2000;
  const [maxUserDurationInSeconds, setMaxUserDurationInSeconds] = useState('');
  const [email, setEmail] = useState(loggedInUser?.attributes?.email || "");
  const [region, setRegion] = useState("us-east-1");
  
  // Region detection state
  const [isRegionDetecting, setIsRegionDetecting] = useState(true);
  const [detectedRegionInfo, setDetectedRegionInfo] = useState(null);
  const [isManualRegionSelection, setIsManualRegionSelection] = useState(false);
  const isManualRegionSelectionRef = useRef(false);
  
  // Fleet capacity parameters
  const [fleetMinCapacity, setFleetMinCapacity] = useState(1);
  const [fleetMaxCapacity, setFleetMaxCapacity] = useState(5);

  // AppStream instance profile selection
  const [useCase, setUseCase] = useState('Office');    // Office | Education | Graphics
  const [tier, setTier] = useState('Basic');           // Basic | Advanced
  const [speed, setSpeed] = useState('Fast');          // Normal | Fast | Faster

  // Lookup table: resolves profile key → instance settings shown as a live preview
  const INSTANCE_MATRIX = {
    'Office:Basic:Normal':       { instance: 'stream.standard.small',        fleetType: 'On-Demand',  session: 'Single', duration: 28800 },
    'Office:Basic:Fast':         { instance: 'stream.standard.medium',       fleetType: 'On-Demand',  session: 'Single', duration: 36000 },
    'Office:Basic:Faster':       { instance: 'stream.standard.large',        fleetType: 'Always-On',  session: 'Single', duration: 43200 },
    'Office:Advanced:Normal':    { instance: 'stream.standard.medium',       fleetType: 'On-Demand',  session: 'Multi',  duration: 36000 },
    'Office:Advanced:Fast':      { instance: 'stream.compute.large',         fleetType: 'On-Demand',  session: 'Multi',  duration: 43200 },
    'Office:Advanced:Faster':    { instance: 'stream.compute.xlarge',        fleetType: 'Always-On',  session: 'Multi',  duration: 43200 },
    'Education:Basic:Normal':    { instance: 'stream.standard.small',        fleetType: 'On-Demand',  session: 'Multi',  duration: 14400 },
    'Education:Basic:Fast':      { instance: 'stream.standard.medium',       fleetType: 'On-Demand',  session: 'Multi',  duration: 21600 },
    'Education:Basic:Faster':    { instance: 'stream.standard.large',        fleetType: 'Always-On',  session: 'Multi',  duration: 21600 },
    'Education:Advanced:Normal': { instance: 'stream.compute.large',         fleetType: 'On-Demand',  session: 'Multi',  duration: 28800 },
    'Education:Advanced:Fast':   { instance: 'stream.graphics.g4dn.large',   fleetType: 'On-Demand',  session: 'Multi',  duration: 28800 },
    'Education:Advanced:Faster': { instance: 'stream.graphics.g4dn.xlarge',  fleetType: 'Always-On',  session: 'Multi',  duration: 36000 },
    'Graphics:Basic:Normal':     { instance: 'stream.graphics.g4dn.large',   fleetType: 'On-Demand',  session: 'Single', duration: 28800 },
    'Graphics:Basic:Fast':       { instance: 'stream.graphics.g4dn.xlarge',  fleetType: 'On-Demand',  session: 'Single', duration: 36000 },
    'Graphics:Basic:Faster':     { instance: 'stream.graphics.g4dn.2xlarge', fleetType: 'Always-On',  session: 'Single', duration: 43200 },
    'Graphics:Advanced:Normal':  { instance: 'stream.graphics.g5.xlarge',    fleetType: 'On-Demand',  session: 'Single', duration: 43200 },
    'Graphics:Advanced:Fast':    { instance: 'stream.graphics.g5.2xlarge',   fleetType: 'On-Demand',  session: 'Single', duration: 43200 },
    'Graphics:Advanced:Faster':  { instance: 'stream.graphics.g5.4xlarge',   fleetType: 'Always-On',  session: 'Single', duration: 43200 },
  };

  const resolvedProfile = INSTANCE_MATRIX[`${useCase}:${tier}:${speed}`] || INSTANCE_MATRIX['Office:Basic:Fast'];
  
  // Parameter consistency - NamePrefix (auto-generated or manual)
  const [namePrefix, setNamePrefix] = useState("");
  const [autoGeneratePrefix, setAutoGeneratePrefix] = useState(true);

  const normalizedPinnedNamePrefix = String(pinnedNamePrefix || "").trim();
  const isNamePrefixPinned = Boolean(normalizedPinnedNamePrefix);

  useEffect(() => {
    // Only set email if it's currently empty, to avoid overwriting user input
    if (loggedInUser?.attributes?.email && !email) {
      setEmail(loggedInUser.attributes.email);
    }
  }, [loggedInUser, email]);

  useEffect(() => {
    if (!isNamePrefixPinned) {
      return;
    }

    setAutoGeneratePrefix(false);
    setNamePrefix(normalizedPinnedNamePrefix);
  }, [isNamePrefixPinned, normalizedPinnedNamePrefix]);

  // Data Compliance parameters
  const [showComplianceSection, setShowComplianceSection] = useState(false);
  const [enableComplianceControls, setEnableComplianceControls] = useState(false);
  const [enableDropboxObjectLock, setEnableDropboxObjectLock] = useState(false);
  const [dropboxObjectLockRetentionMode, setDropboxObjectLockRetentionMode] = useState("GOVERNANCE");
  const [dropboxObjectLockRetentionDays, setDropboxObjectLockRetentionDays] = useState(30);
  const [enableDropboxChecksumValidation, setEnableDropboxChecksumValidation] = useState(false);
  const [enableSharedFolderObjectLock, setEnableSharedFolderObjectLock] = useState(false);
  const [sharedFolderObjectLockRetentionMode, setSharedFolderObjectLockRetentionMode] = useState("GOVERNANCE");
  const [sharedFolderObjectLockRetentionDays, setSharedFolderObjectLockRetentionDays] = useState(30);
  const [enableUserDataObjectLock, setEnableUserDataObjectLock] = useState(false);
  const [userDataObjectLockRetentionMode, setUserDataObjectLockRetentionMode] = useState("GOVERNANCE");
  const [userDataObjectLockRetentionDays, setUserDataObjectLockRetentionDays] = useState(30);
  const [enableS3AccessLogging, setEnableS3AccessLogging] = useState(false);

  // Validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmed = (email || '').trim();
    if (!trimmed) {
      return "Email is required";
    }
    if (trimmed.length > 254) {
      // RFC 5321 — reject overlong addresses (DoS / abuse guard).
      return "Email is too long";
    }
    if (!emailRegex.test(trimmed)) {
      return "Please enter a valid email address";
    }
    // Professional email policy: reject free webmail and disposable providers.
    if (isFreeOrDisposableEmail(trimmed)) {
      return "Please use your professional (work) email address — free webmail and temporary-mail providers are not accepted.";
    }
    return "";
  };

  const validateCompanyName = (name) => {
    if (!name.trim()) {
      return "Company name is required";
    }
    if (name.trim().length < 2) {
      return "Company name must be at least 2 characters long";
    }
    if (name.trim().length > 50) {
      return "Company name must be less than 50 characters";
    }
    // Check for potentially harmful characters
    const dangerousChars = /[<>"'&]/;
    if (dangerousChars.test(name)) {
      return "Company name contains invalid characters";
    }
    return "";
  };

  const validateMaxUserDuration = (duration) => {
    if (!duration.toString().trim()) {
      return "Max user duration is required";
    }
    const numDuration = parseInt(duration);
    if (isNaN(numDuration)) {
      return "Duration must be a valid number";
    }
    if (numDuration < 300) { // 5 minutes minimum
      return "Duration must be at least 300 seconds (5 minutes)";
    }
    if (numDuration > 43200) { // 12 hours maximum
      return "Duration must be less than 43200 seconds (12 hours)";
    }
    return "";
  };

  const validateRegion = (region) => {
    if (!region || region === "") {
      return "Please select an AWS region";
    }
    
    // Validate against allowed regions
    const allowedRegions = ['us-east-1', 'us-east-2', 'us-west-2'];
    if (!allowedRegions.includes(region)) {
      return "Please select a valid AWS region from the available options";
    }
    
    return "";
  };

  const validateFleetMinCapacity = (minCapacity) => {
    if (!minCapacity && minCapacity !== 0) {
      return "Fleet minimum capacity is required";
    }
    const numCapacity = parseInt(minCapacity);
    if (isNaN(numCapacity)) {
      return "Fleet minimum capacity must be a valid number";
    }
    if (numCapacity < 1) {
      return "Fleet minimum capacity must be at least 1";
    }
    if (numCapacity > 100) {
      return "Fleet minimum capacity must be less than or equal to 100";
    }
    return "";
  };

  const validateFleetMaxCapacity = (maxCapacity, minCapacity, currentUseCase) => {
    if (!maxCapacity && maxCapacity !== 0) {
      return "Maximum number of concurrent users is required";
    }
    const numMaxCapacity = parseInt(maxCapacity);
    const numMinCapacity = parseInt(minCapacity);
    
    if (isNaN(numMaxCapacity)) {
      return "Maximum number of concurrent users must be a valid number";
    }
    if (numMaxCapacity < 1) {
      return "Maximum number of concurrent users must be at least 1";
    }
    if (currentUseCase === 'Graphics' && numMaxCapacity > 25) {
      return "Limit for graphic instances is 25. Please contact support at 800-985-1351 for larger selections.";
    }
    if (numMaxCapacity > 100) {
      return "Limit is 100 concurrent users. Please contact support at 800-985-1351 for larger selections.";
    }
    if (!isNaN(numMinCapacity) && numMaxCapacity < numMinCapacity) {
      return "Maximum number of concurrent users must be greater than or equal to the minimum";
    }
    return "";
  };

  const validateNamePrefix = useCallback((prefix) => {
    if (!autoGeneratePrefix) {
      if (!prefix.trim()) {
        return "Name prefix is required when auto-generation is disabled";
      }
      if (!/^[A-Za-z0-9-]{1,43}$/.test(prefix.trim())) {
        return "Name prefix must contain only letters, numbers, and hyphens (1-43 characters)";
      }
    }
    return "";
  }, [autoGeneratePrefix]);

  // Validate individual field
  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case 'email':
        return validateEmail(value);
      case 'companyName':
        return validateCompanyName(value);
      case 'maxUserDurationInSeconds':
        return validateMaxUserDuration(value);
      case 'region':
        return validateRegion(value);
      case 'fleetMinCapacity':
        return validateFleetMinCapacity(value);
      case 'fleetMaxCapacity':
        return validateFleetMaxCapacity(value, fleetMinCapacity, useCase);
      case 'namePrefix':
        return validateNamePrefix(value);
      default:
        return "";
    }
  };

  // Validate entire form
  const validateForm = () => {
    const newErrors = {
      email: validateEmail(email),
      companyName: validateCompanyName(companyName),
      maxUserDurationInSeconds: validateMaxUserDuration(maxUserDurationInSeconds),
      region: validateRegion(region),
      fleetMinCapacity: validateFleetMinCapacity(fleetMinCapacity),
      fleetMaxCapacity: validateFleetMaxCapacity(fleetMaxCapacity, fleetMinCapacity, useCase),
      namePrefix: validateNamePrefix(namePrefix)
    };

    setErrors(newErrors);

    // Check if form is valid (no errors)
    const formIsValid = Object.values(newErrors).every(error => error === "");

    return formIsValid;
  };

  // Input change handlers with validation
  const handleCompanyNameChange = (e) => {
    const value = e.target.value;
    setCompanyName(value);
    setTouched(prev => ({ ...prev, companyName: true }));
    
    const error = validateField('companyName', value);
    setErrors(prev => ({ ...prev, companyName: error }));
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setTouched(prev => ({ ...prev, email: true }));
    
    const error = validateField('email', value);
    setErrors(prev => ({ ...prev, email: error }));
  };

  const handleMaxUserDurationChange = (e) => {
    const value = e.target.value;
    setMaxUserDurationInSeconds(value);
    setTouched(prev => ({ ...prev, maxUserDurationInSeconds: true }));
    
    const error = validateField('maxUserDurationInSeconds', value);
    setErrors(prev => ({ ...prev, maxUserDurationInSeconds: error }));
  };

  const handleRegionChange = (e) => {
    const value = e.target.value;
    isManualRegionSelectionRef.current = true;
    setRegion(value);
    setTouched(prev => ({ ...prev, region: true }));
    setIsManualRegionSelection(true); // User manually selected a region
    
    const error = validateField('region', value);
    setErrors(prev => ({ ...prev, region: error }));
  };

  // Handle region suggestion from automatic detection
  const handleRegionSuggestion = useCallback((suggestedRegion, detectionInfo) => {
    if (isManualRegionSelectionRef.current) {
      setIsRegionDetecting(false);
      return;
    }

    setRegion(suggestedRegion);
    setDetectedRegionInfo(detectionInfo);
    setIsRegionDetecting(false);

    const error = validateRegion(suggestedRegion);
    setErrors(prev => ({ ...prev, region: error }));
  }, []);

  // Auto-detect optimal region on component mount
  useEffect(() => {
    let isCancelled = false;

    const detectOptimalRegion = async () => {
      if (isManualRegionSelectionRef.current) {
        setIsRegionDetecting(false);
        return;
      }

      try {
        setIsRegionDetecting(true);
        const result = await getOptimalRegion();

        if (isCancelled || isManualRegionSelectionRef.current) {
          return;
        }

        setDetectedRegionInfo(result);
        setRegion(result.region);

        const error = validateRegion(result.region);
        setErrors(prev => ({ ...prev, region: error }));
      } catch (error) {
        console.error('Failed to detect optimal region:', error);
        // Keep default region (us-east-1)
      } finally {
        if (!isCancelled) {
          setIsRegionDetecting(false);
        }
      }
    };

    detectOptimalRegion();

    return () => {
      isCancelled = true;
    };
  }, []);

  const handleFleetMinCapacityChange = (e) => {
    const value = e.target.value;
    setFleetMinCapacity(value);
    setTouched(prev => ({ ...prev, fleetMinCapacity: true }));
    
    const error = validateField('fleetMinCapacity', value);
    setErrors(prev => ({ ...prev, fleetMinCapacity: error }));
    
    // Re-validate max capacity when min capacity changes
    if (touched.fleetMaxCapacity) {
      const maxError = validateField('fleetMaxCapacity', fleetMaxCapacity);
      setErrors(prev => ({ ...prev, fleetMaxCapacity: maxError }));
    }
  };

  const handleFleetMaxCapacityChange = (e) => {
    const value = e.target.value;
    setFleetMaxCapacity(value);
    setTouched(prev => ({ ...prev, fleetMaxCapacity: true }));
    
    const error = validateField('fleetMaxCapacity', value);
    setErrors(prev => ({ ...prev, fleetMaxCapacity: error }));
  };

  const handleUseCaseChange = (e) => {
    const newUseCase = e.target.value;
    setUseCase(newUseCase);
    if (newUseCase === 'Graphics') {
      setTouched(prev => ({ ...prev, fleetMaxCapacity: true }));
    }
  };

  const handleNamePrefixChange = (e) => {
    if (isNamePrefixPinned) {
      return;
    }
    const value = e.target.value;
    setNamePrefix(value);
    setTouched((prev) => ({ ...prev, namePrefix: true }));

    const error = validateField('namePrefix', value);
    setErrors((prev) => ({ ...prev, namePrefix: error }));
  };

  const handleAutoGeneratePrefixChange = (e) => {
    if (isNamePrefixPinned) {
      return;
    }
    const isAuto = e.target.checked;
    setAutoGeneratePrefix(isAuto);
    
    if (isAuto) {
      // Clear manual namePrefix when switching to auto
      setNamePrefix("");
      setTouched(prev => ({ ...prev, namePrefix: false }));
      setErrors(prev => ({ ...prev, namePrefix: "" }));
    }
  };

  // Auto-generate NamePrefix when company name changes and auto-generation is enabled
  useEffect(() => {
    if (isNamePrefixPinned) {
      return;
    }
    if (autoGeneratePrefix && companyName.trim()) {
      const sanitizedCompanyName = companyName
        .trim()
        .replace(/[^a-zA-Z0-9-]/g, '') // Remove special chars except hyphens
        .substring(0, 20); // Limit length
      
      const timestamp = Date.now().toString().slice(-8);
      const basePrefix = sanitizedCompanyName ? `${sanitizedCompanyName}-${timestamp}` : timestamp;
      const generatedPrefix = basePrefix.slice(0, 43);

      setNamePrefix(generatedPrefix);
    }
  }, [companyName, autoGeneratePrefix, isNamePrefixPinned]);

  // Update form validity whenever fields change
  useEffect(() => {
    const newErrors = {
      email: validateEmail(email),
      companyName: validateCompanyName(companyName),
      maxUserDurationInSeconds: validateMaxUserDuration(maxUserDurationInSeconds),
      region: validateRegion(region),
      fleetMinCapacity: validateFleetMinCapacity(fleetMinCapacity),
      fleetMaxCapacity: validateFleetMaxCapacity(fleetMaxCapacity, fleetMinCapacity, useCase),
      namePrefix: validateNamePrefix(namePrefix)
    };
    
    setErrors(newErrors);
  }, [email, companyName, maxUserDurationInSeconds, region, fleetMinCapacity, fleetMaxCapacity, namePrefix, autoGeneratePrefix, validateNamePrefix, useCase]);

  // Sync maxUserDurationInSeconds with the resolved profile whenever use case/tier/speed changes
  useEffect(() => {
    if (resolvedProfile && !touched.maxUserDurationInSeconds) {
      setMaxUserDurationInSeconds(resolvedProfile.duration);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useCase, tier, speed]);

  const createAccount = async (companyName, email, region, maxUserDurationInSeconds, fleetMinCapacity, fleetMaxCapacity, namePrefix, stripeCustomerIdParam, ceilingDepositCentsParam, stripePaymentIntentIdParam, requestIdParam, stripePaymentMethodIdParam) => {
    console.log("🚀 createAccount function called");
    console.trace("createAccount stack trace:");
    
    try {
      // const session = await Auth.currentSession();
      // const token = session.getIdToken().getJwtToken();
      let token = null;
      try {
        if (loggedInUser) {
          token = await getToken({ expectedEmail: loggedInUser?.attributes?.email, user: loggedInUser });
          console.log("✅ Got auth token");
        } else {
          console.log("ℹ️ No logged in user, proceeding without auth token");
        }
      } catch (err) {
        console.warn("Failed to get auth token, proceeding without it", err);
      }
      
      const apiName = "accountAppBackend"; // replace this with your api name.
      const path = "/account"; //replace this with the path you have configured on your API
      const createAccountInit = {
        body: {
          account_email: email,
          company_name: companyName,
          region_name: region,
          max_user_duration_in_seconds: maxUserDurationInSeconds,
          fleet_min_capacity: parseInt(fleetMinCapacity),
          fleet_max_capacity: parseInt(fleetMaxCapacity),
          name_prefix: namePrefix, // Ensure consistency
          requester_email: loggedInUser?.attributes?.email,
          // The backend /payment-intent endpoint does not return a customer object, so we supply a placeholder to pass downstream 400 validation:
          stripe_customer_id: stripeCustomerIdParam || 'cus_GuestCheckout',
          // Stripe Elements confirmed PI id (pi_xxx) — backend persists in DDB; webhook is a safety net.
          ...(stripePaymentIntentIdParam ? { stripe_payment_intent_id: stripePaymentIntentIdParam } : {}),
          // Saved PaymentMethod (pm_xxx) for off-session reuse (mid-cycle top-ups, end-of-month settlement).
          ...(stripePaymentMethodIdParam ? { payment_method_id: stripePaymentMethodIdParam } : {}),
          // Request id minted by /payment-intent; lets webhook match if /account arrives later
          ...(requestIdParam ? { request_id: requestIdParam } : {}),
          ceiling_deposit_cents: ceilingDepositCentsParam ? Math.max(parseInt(ceilingDepositCentsParam) || 2000, 2000) : 2000,
          // Data Compliance parameters
          enable_compliance_controls: enableComplianceControls,
          enable_dropbox_object_lock: enableDropboxObjectLock,
          dropbox_object_lock_retention_mode: dropboxObjectLockRetentionMode,
          dropbox_object_lock_retention_days: parseInt(dropboxObjectLockRetentionDays),
          enable_dropbox_checksum_validation: enableDropboxChecksumValidation,
          enable_shared_folder_object_lock: enableSharedFolderObjectLock,
          shared_folder_object_lock_retention_mode: sharedFolderObjectLockRetentionMode,
          shared_folder_object_lock_retention_days: parseInt(sharedFolderObjectLockRetentionDays),
          enable_user_data_object_lock: enableUserDataObjectLock,
          user_data_object_lock_retention_mode: userDataObjectLockRetentionMode,
          user_data_object_lock_retention_days: parseInt(userDataObjectLockRetentionDays),
          enable_shared_folder_encryption: true, // always on — AES256/SSE-S3 applies to every bucket regardless, not a real client choice
          enable_s3_access_logging: enableS3AccessLogging,
          // AppStream instance profile
          use_case: useCase,
          tier: tier,
          speed: speed
        },
        ...(token && {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }),
      };

      console.log("📡 Sending API request to:", apiName, path);
      
      setIsAccountRequested(true);
      const response = await API.post(apiName, path, createAccountInit);
      console.log("✅ Account request created successfully:", response);
      
      // Redirect to Account Activities page to track progress
      const encodedEmail = encodeURIComponent(email);
      const encodedCompanyName = encodeURIComponent(companyName);
      navigate(
        `/status/${encodedEmail}/${encodedCompanyName}`,
        { state: { activityType: 'creation' } }
      );
      
      fetchAccountStatusList();
      return response;
      
    } catch (error) {
      console.error("❌ Error creating account:", error.message);
      
      setIsAccountRequested(false);
      
      // More user-friendly error messages
      if (error.response) {
        setSubmitError(error.response.data?.message || error.response.statusText || 'Failed to create account request');
      } else if (error.message) {
        setSubmitError(error.message);
      } else {
        setSubmitError('Failed to submit the account request. Please try again.');
      }
      
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // OWASP A04 client-side rate limit: reject rapid-fire double clicks before
    // any state changes or network calls. Synchronous ref guard + min interval.
    if (submitInFlightRef.current) {
      console.warn('Submit ignored: previous request still in flight');
      return;
    }
    const now = Date.now();
    if (now - lastSubmitAtRef.current < MIN_SUBMIT_INTERVAL_MS) {
      setSubmitError('Please wait a moment before retrying.');
      return;
    }
    submitInFlightRef.current = true;
    lastSubmitAtRef.current = now;

    try {
      await handleSubmitInner(e);
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const handleSubmitInner = async (e) => {
    console.log("=== Form Submit Started ===");
    
    // Mark all fields as touched to show validation errors
    setTouched({
      companyName: true,
      email: true,
      maxUserDurationInSeconds: true,
      region: true,
      fleetMinCapacity: true,
      fleetMaxCapacity: true,
      namePrefix: true
    });
    setSubmitError(null);

    // Validate the entire form
    const isValid = validateForm();
    console.log("Form validation result:", isValid, "Errors:", errors);
    
    if (!isValid) {
      console.log("❌ Form validation failed", errors);
      // Inline error messages are already shown per-field via the touched state above.
      return;
    }

    if (!acceptedTerms) {
      console.log("❌ Terms of Service not accepted");
      setSubmitError("You must read and agree to the Terms of Service before creating an account.");
      return;
    }

    // Check if user already has an active request (enforce one account per user)
    const userEmail = loggedInUser?.attributes?.email;
    if (userEmail && accountList && accountList.length > 0) {
      const activeStatuses = [
        'PENDING',
        'PAYMENT_SUCCESSFUL',
        'APPROVED',
        'CREATING_ACCOUNT',
        'ACCOUNT_CREATED',
        'DEPLOYING_INFRASTRUCTURE',
        'INFRASTRUCTURE_DEPLOYED',
        'CREATING_DASHBOARD',
        'DASHBOARD_DEPLOYED',
        'COMPLETED'
      ];
      const existingActiveRequest = accountList.find(acc => 
        acc.requester_email?.toLowerCase() === userEmail.toLowerCase() &&
        activeStatuses.includes(acc.request_status)
      );
      
      if (existingActiveRequest) {
        console.log("❌ Duplicate request prevented - user already has active request");
        setSubmitError(`You already have an active account request (status: ${existingActiveRequest.request_status}). Only one account per user is allowed. Account: ${existingActiveRequest.account_email} — ${existingActiveRequest.company_name}`);
        return;
      }
    }

    // Sanitize inputs before sending
    const sanitizedCompanyName = companyName.trim();
    const sanitizedEmail = email.trim().toLowerCase();
    const sanitizedDuration = parseInt(maxUserDurationInSeconds);
    const sanitizedMinCapacity = parseInt(fleetMinCapacity);
    const sanitizedMaxCapacity = parseInt(fleetMaxCapacity);
    const sanitizedNamePrefix = namePrefix.trim();

    console.log("Sanitized data extracted.");

    // Additional security check
    if (!sanitizedCompanyName || !sanitizedEmail || !region || 
        isNaN(sanitizedDuration) || isNaN(sanitizedMinCapacity) || isNaN(sanitizedMaxCapacity) ||
        (!autoGeneratePrefix && !sanitizedNamePrefix)) {
      console.error("❌ Invalid form data after sanitization");
      setSubmitError("The form contains invalid data. Please review all highlighted fields and try again.");
      return;
    }

    // We now start the payment intent flow instead of creating directly
    try {
      console.log("💳 Fetching client secret...");
      let token = null;
      if (loggedInUser) {
        token = await getToken({ expectedEmail: loggedInUser?.attributes?.email, user: loggedInUser });
      }
      
      const res = await fetch("https://sduqrjd0v2.execute-api.us-east-1.amazonaws.com/dev/payment-intent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        body: JSON.stringify({
          capacity: parseInt(fleetMaxCapacity) || 1,
          duration: parseInt(maxUserDurationInSeconds) || 3600,
          tier: tier,
          useCase: useCase,
          speed: speed,
          company_name: companyName ? companyName.trim() : undefined,
          checkoutMode,
          // Extra provisioning fields used by embedded path to populate session metadata
          account_email: email ? email.trim().toLowerCase() : undefined,
          region_name: region || undefined,
          fleet_min_capacity: parseInt(fleetMinCapacity) || 1
        })
      });
      
      if (!res.ok) throw new Error("Payment initialization failed.");
      
      const data = await res.json();
      console.log("💳 Payment intent data:", data);
      setClientSecret(data.clientSecret);
      setCheckoutAmount(data.amount);
      setCheckoutBreakdown({
        setupFeeCents: data.setupFeeCents,
        escrowDepositCents: data.escrowDepositCents,
        effectivePerUserCents: data.effectivePerUserCents,
        tierLabel: data.tierLabel,
        tiers: data.tiers,
        capacity: data.capacity,
        profileKey: data.profileKey
      });
      setStripeCustomerId(data.stripeCustomerId || data.customer || data.customer_id || data.stripe_customer_id || null);
      setPaymentRequestId(data.request_id || null);
      setCheckoutMode(data.checkoutMode || 'elements');
      setSessionId(data.sessionId || null);
      setShowCheckout(true);
    } catch (error) {
      console.error("❌ Error initiating payment:", error);
      setSubmitError("Could not connect to payment processor.");
    }
  };

  // useEffect(() => {
  //   async function fetchAccountList() {
  //     const accountList = await getEmailAccountStatus(loggedInUser);
  //     if (!accountList.length) {
  //       setIsAccountRequested(false);
  //     }
  //   }
  //   fetchAccountList();
  // }, [loggedInUser]);

  const handleEmbeddedComplete = useCallback(async () => {
    try {
      const session = await API.get("accountAppBackend", "/checkout-session", {
        queryStringParameters: { session_id: sessionId }
      });
      setShowCheckout(false);
      const escrowCents = (checkoutBreakdown && checkoutBreakdown.escrowDepositCents > 0)
        ? checkoutBreakdown.escrowDepositCents
        : Math.max((checkoutAmount || 2000) - (checkoutBreakdown?.setupFeeCents || 0), 2000);
      await createAccount(
        companyName.trim(),
        email.trim().toLowerCase(),
        region,
        parseInt(maxUserDurationInSeconds),
        parseInt(fleetMinCapacity),
        parseInt(fleetMaxCapacity),
        namePrefix.trim(),
        stripeCustomerId,
        escrowCents,
        session.paymentIntentId,
        paymentRequestId,
        session.paymentMethodId
      );
    } catch (err) {
      console.error("❌ Error completing embedded checkout:", err);
    }
  }, [sessionId, checkoutBreakdown, checkoutAmount, companyName, email, region,
      maxUserDurationInSeconds, fleetMinCapacity, fleetMaxCapacity, namePrefix,
      stripeCustomerId, paymentRequestId, createAccount]);

  const hasApprovedAccount = accountList.some((account) => {
    const status = (account.request_status || account.status || "").toUpperCase();
    return status === "APPROVED" || status === "COMPLETED";
  });

  // Debug: Log button state
  const isButtonDisabled =
    (!isAdmin && hasApprovedAccount) ||
    isAccountRequested ||
    !acceptedTerms ||
    !email.trim() ||
    !companyName.trim() ||
    !maxUserDurationInSeconds ||
    !region ||
    !fleetMinCapacity ||
    !fleetMaxCapacity ||
    // Only check errors for touched fields
    (touched.email && errors.email) ||
    (touched.companyName && errors.companyName) ||
    (touched.maxUserDurationInSeconds && errors.maxUserDurationInSeconds) ||
    (touched.region && errors.region) ||
    (touched.fleetMinCapacity && errors.fleetMinCapacity) ||
    (touched.fleetMaxCapacity && errors.fleetMaxCapacity) ||
    (touched.namePrefix && errors.namePrefix);

  return (
    (isAdmin || !hasApprovedAccount) && (
      <>
      {showCheckout && clientSecret && stripePromise && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", zIndex: 9999,
          display: "flex", justifyContent: "center", alignItems: "flex-start",
          padding: "5vh 16px 16px", overflowY: "auto"
        }}>
          <div style={{ background: "white", borderRadius: "8px", width: "100%", maxWidth: "480px", boxSizing: "border-box", overflow: "hidden" }}>
            {checkoutMode === 'embedded' ? (
              <StripeCheckoutForm
                checkoutMode="embedded"
                stripePromise={stripePromise}
                clientSecret={clientSecret}
                amountCents={checkoutAmount}
                breakdown={checkoutBreakdown}
                capacity={fleetMaxCapacity}
                duration={maxUserDurationInSeconds}
                tier={tier}
                useCase={useCase}
                speed={speed}
                userEmail={email || loggedInUser?.attributes?.email}
                onCancel={() => setShowCheckout(false)}
                onComplete={handleEmbeddedComplete}
              />
            ) : (
              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <StripeCheckoutForm
                  checkoutMode="elements"
                  amountCents={checkoutAmount}
                  breakdown={checkoutBreakdown}
                  capacity={fleetMaxCapacity}
                  duration={maxUserDurationInSeconds}
                  tier={tier}
                  useCase={useCase}
                  speed={speed}
                  userEmail={email || loggedInUser?.attributes?.email}
                  onCancel={() => setShowCheckout(false)}
                  onSuccess={async (intent) => {
                    setShowCheckout(false);

                    // ceiling_deposit_cents must be the escrow-only portion (the AWS usage budget/ceiling),
                    // NOT the total checkout amount which also includes the setup/service fee.
                    // Use the server-authoritative escrowDepositCents from the payment intent response.
                    const escrowCents = (checkoutBreakdown && checkoutBreakdown.escrowDepositCents > 0)
                      ? checkoutBreakdown.escrowDepositCents
                      : Math.max((checkoutAmount || 2000) - (checkoutBreakdown?.setupFeeCents || 0), 2000);
                    const stripePaymentIntentId = (intent && intent.id) ? intent.id : null;
                    // payment_method is the saved pm_xxx attached by setup_future_usage='off_session'
                    // on the server-side PaymentIntent — required for mid-cycle / end-of-month charges.
                    const stripePaymentMethodId = (intent && intent.payment_method) ? intent.payment_method : null;

                    await createAccount(
                      companyName.trim(),
                      email.trim().toLowerCase(),
                      region,
                      parseInt(maxUserDurationInSeconds),
                      parseInt(fleetMinCapacity),
                      parseInt(fleetMaxCapacity),
                      namePrefix.trim(),
                      stripeCustomerId,
                      escrowCents,
                      stripePaymentIntentId,
                      paymentRequestId,
                      stripePaymentMethodId
                    );
                  }}
                />
              </Elements>
            )}
          </div>
        </div>
      )}
        <h2>Request Account</h2>
        <form className="create-account-form" onSubmit={handleSubmit}>
          <div className="input-group">
            <input
              type="text"
              placeholder="Company Name"
              value={companyName}
              onChange={handleCompanyNameChange}
              className={`form-input ${touched.companyName && errors.companyName ? 'error' : ''}`}
              aria-label="Company Name"
              aria-describedby={errors.companyName ? "companyName-error" : undefined}
            />
            {touched.companyName && errors.companyName && (
              <span className="error-message" id="companyName-error" role="alert">
                {errors.companyName}
              </span>
            )}
          </div>

          <div className="input-group">
            <input
              type="email"
              placeholder="Professional email (e.g. you@yourcompany.com)"
              value={email}
              onChange={handleEmailChange}
              className={`form-input ${touched.email && errors.email ? 'error' : ''}`}
              aria-label="Professional email address"
              aria-describedby={errors.email ? "email-error" : "email-help"}
              maxLength={254}
            />
            {!(touched.email && errors.email) && (
              <span className="input-help" id="email-help">
                Use your work email — Gmail, Yahoo, Outlook, and other free or temporary providers are not accepted.
              </span>
            )}
            {touched.email && errors.email && (
              <span className="error-message" id="email-error" role="alert">
                {errors.email}
              </span>
            )}
          </div>

          <div className="input-group">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="autoGeneratePrefix"
                checked={autoGeneratePrefix}
                onChange={handleAutoGeneratePrefixChange}
                className="form-checkbox"
                disabled={isNamePrefixPinned}
              />
              <label htmlFor="autoGeneratePrefix" className="checkbox-label">
                Auto-generate Name Prefix (recommended)
                <button className="faq-trigger" onClick={(e) => { e.preventDefault(); setActiveFaq('subdomain'); }}>!</button>
              </label>
            </div>
            
            {!autoGeneratePrefix && (
              <input
                type="text"
                placeholder="Name Prefix (1-43 characters, letters/numbers/hyphens only)"
                value={namePrefix}
                onChange={handleNamePrefixChange}
                className={`form-input ${touched.namePrefix && errors.namePrefix ? 'error' : ''}`}
                aria-label="Name Prefix"
                aria-describedby={errors.namePrefix ? "namePrefix-error" : undefined}
                maxLength="43"
                disabled={isNamePrefixPinned}
                readOnly={isNamePrefixPinned}
              />
            )}
            
            {autoGeneratePrefix && namePrefix && (
              <div className="generated-prefix-display">
                <span className="prefix-label">Generated Prefix:</span>
                <span className="prefix-value">{namePrefix}</span>
              </div>
            )}

            {isNamePrefixPinned && !autoGeneratePrefix && (
              <div className="generated-prefix-display">
                <span className="prefix-label">Pinned Prefix:</span>
                <span className="prefix-value">{namePrefix}</span>
              </div>
            )}
            
            {touched.namePrefix && errors.namePrefix && (
              <span className="error-message" id="namePrefix-error" role="alert">
                {errors.namePrefix}
              </span>
            )}
          </div>

          <div className="input-group">
            <label htmlFor="maxUserDuration" className="form-label">
              Session Length Limit
            </label>
            <input
              id="maxUserDuration"
              type="number"
              placeholder="Enter seconds (300 – 43 200)"
              value={maxUserDurationInSeconds}
              onChange={handleMaxUserDurationChange}
              min="300"
              max="43200"
              className={`form-input ${touched.maxUserDurationInSeconds && errors.maxUserDurationInSeconds ? 'error' : ''}`}
              aria-describedby={errors.maxUserDurationInSeconds ? "duration-error" : "duration-hint"}
            />
            {!errors.maxUserDurationInSeconds && maxUserDurationInSeconds > 0 && (
              <span className="input-hint" id="duration-hint">
                {(() => {
                  const s = parseInt(maxUserDurationInSeconds);
                  const h = Math.floor(s / 3600);
                  const m = Math.round((s % 3600) / 60);
                  return h > 0 && m > 0
                    ? `Each user session will end after ${h}h ${m}m`
                    : h > 0
                    ? `Each user session will end after ${h} hour${h !== 1 ? 's' : ''}`
                    : `Each user session will end after ${m} minute${m !== 1 ? 's' : ''}`;
                })()}
              </span>
            )}
            {touched.maxUserDurationInSeconds && errors.maxUserDurationInSeconds && (
              <span className="error-message" id="duration-error" role="alert">
                {errors.maxUserDurationInSeconds}
              </span>
            )}
          </div>

          <div className="input-group">
            <label htmlFor="fleetMinCapacity" className="form-label">
              Minimum Workstations (Always Running)
            </label>
            <input
              id="fleetMinCapacity"
              type="number"
              placeholder="Enter a number (1 – 100)"
              value={fleetMinCapacity}
              onChange={handleFleetMinCapacityChange}
              min="1"
              max="100"
              className={`form-input ${touched.fleetMinCapacity && errors.fleetMinCapacity ? 'error' : ''}`}
              aria-describedby={errors.fleetMinCapacity ? "fleetMinCapacity-error" : "fleetMin-hint"}
            />
            {!errors.fleetMinCapacity && fleetMinCapacity > 0 && (
              <span className="input-hint" id="fleetMin-hint">
                {parseInt(fleetMinCapacity) === 1
                  ? '1 workstation will always be on — users can connect instantly'
                  : `${fleetMinCapacity} workstations will always be on — users can connect instantly`}
              </span>
            )}
            {touched.fleetMinCapacity && errors.fleetMinCapacity && (
              <span className="error-message" id="fleetMinCapacity-error" role="alert">
                {errors.fleetMinCapacity}
              </span>
            )}
          </div>

          <div className="input-group">
            <label htmlFor="fleetMaxCapacity" className="form-label">
              Maximum Concurrent Users
            </label>
            <input
              id="fleetMaxCapacity"
              type="number"
              placeholder="Enter a number (1 – 100)"
              value={fleetMaxCapacity}
              onChange={handleFleetMaxCapacityChange}
              min="1"
              max="100"
              className={`form-input ${touched.fleetMaxCapacity && errors.fleetMaxCapacity ? 'error' : ''}`}
              aria-describedby={errors.fleetMaxCapacity ? "fleetMaxCapacity-error" : "fleetMax-hint"}
            />
            {!errors.fleetMaxCapacity && fleetMaxCapacity > 0 && (
              parseInt(fleetMaxCapacity) < 2 ? (
                <span className="input-warning" id="fleetMax-hint" role="alert">
                  Warning: You need at least 2 users to use a workspace — the admin account alone is not permitted to use a workspace.
                </span>
              ) : (
                <span className="input-hint" id="fleetMax-hint">
                  {parseInt(fleetMaxCapacity) - 1 === 1
                    ? 'Up to 1 user can work on the system at the same time (1 slot is reserved for the admin account, which cannot use a workspace)'
                    : `Up to ${fleetMaxCapacity - 1} users can work on the system at the same time (1 slot is reserved for the admin account, which cannot use a workspace)`}
                </span>
              )
            )}
            {touched.fleetMaxCapacity && errors.fleetMaxCapacity && (
              <span className="error-message" id="fleetMaxCapacity-error" role="alert">
                {errors.fleetMaxCapacity}
              </span>
            )}
          </div>
          {/* <label
        for="countries"
        class="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
      >
        Select an option
      </label> */}
          <div className="input-group">
            <label htmlFor="region" className="form-label">
              AWS Region
              <button className="faq-trigger" onClick={(e) => { e.preventDefault(); setActiveFaq('region'); }}>!</button>
              {isRegionDetecting && (
                <span className="detection-status"> (Detecting optimal region...)</span>
              )}
              {!isRegionDetecting && detectedRegionInfo && detectedRegionInfo.method !== 'default' && (
                <span className="detection-info">
                  {detectedRegionInfo.method === 'geolocation' ? ' 🌍 Auto-detected from your location' : 
                   detectedRegionInfo.method === 'ip' ? ` 🌐 Suggested for ${detectedRegionInfo.city || 'your area'}` : ''}
                </span>
              )}
            </label>
            <select
              id="region"
              onChange={handleRegionChange}
              value={region}
              className={`form-input ${touched.region && errors.region ? 'error' : ''} bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500`}
              aria-label="AWS Region"
              aria-describedby={errors.region ? "region-error" : undefined}
            >
              <RegionList onRegionSuggestion={handleRegionSuggestion} showLocationInfo={true} />
            </select>
            {touched.region && errors.region && (
              <span className="error-message" id="region-error" role="alert">
                {errors.region}
              </span>
            )}
          </div>

          {/* Workstation Specification Section */}
          <div className="instance-profile-section" style={{ marginTop: '20px' }}>
            <div
              style={{
                padding: '12px 15px',
                backgroundColor: '#eef4ff',
                borderRadius: '8px 8px 0 0',
                border: '1px solid #c7d9f5',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1a3a6b' }}>
                🖥️ Workstation Specification
                <button className="faq-trigger" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveFaq('fleet'); }}>!</button>
              </h3>
              <span style={{
                fontSize: '12px',
                backgroundColor: '#1a3a6b',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '10px'
              }}>
                {resolvedProfile.instance}
              </span>
            </div>

            <div style={{
              padding: '16px 15px',
              border: '1px solid #c7d9f5',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              backgroundColor: '#f8faff'
            }}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {/* Use Case */}
                <div className="input-group" style={{ flex: 1, minWidth: '140px' }}>
                  <label htmlFor="useCase" className="form-label" style={{ fontWeight: '600' }}>
                    Use Case
                  </label>
                  <select
                    id="useCase"
                    value={useCase}
                    onChange={handleUseCaseChange}
                    className="form-input bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                  >
                    <option value="Office">Office</option>
                    <option value="Education">Education</option>
                    <option value="Graphics">Graphics</option>
                  </select>
                </div>

                {/* Tier */}
                <div className="input-group" style={{ flex: 1, minWidth: '120px' }}>
                  <label htmlFor="tier" className="form-label" style={{ fontWeight: '600' }}>
                    Tier
                  </label>
                  <select
                    id="tier"
                    value={tier}
                    onChange={(e) => setTier(e.target.value)}
                    className="form-input bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                  >
                    <option value="Basic">Basic</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>

                {/* Speed */}
                <div className="input-group" style={{ flex: 1, minWidth: '120px' }}>
                  <label htmlFor="speed" className="form-label" style={{ fontWeight: '600' }}>
                    Speed
                  </label>
                  <select
                    id="speed"
                    value={speed}
                    onChange={(e) => setSpeed(e.target.value)}
                    className="form-input bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                  >
                    <option value="Normal">Normal — Cost-efficient</option>
                    <option value="Fast">Fast — Balanced</option>
                    <option value="Faster">Faster — Max performance</option>
                  </select>
                </div>
              </div>

              {/* Live profile preview */}
              <div style={{
                marginTop: '12px',
                padding: '10px 14px',
                backgroundColor: '#fff',
                border: '1px solid #c7d9f5',
                borderRadius: '6px',
                fontSize: '13px',
                display: 'flex',
                gap: '20px',
                flexWrap: 'wrap',
                color: '#333'
              }}>
                <span><strong>Instance:</strong> {resolvedProfile.instance}</span>
                <span><strong>Fleet:</strong> {resolvedProfile.fleetType}</span>
                <span><strong>Sessions:</strong> {resolvedProfile.session}</span>
                <span><strong>Max session:</strong> {Math.round(resolvedProfile.duration / 3600)}h</span>
              </div>
            </div>
          </div>

          {/* Data Compliance Section - Collapsible */}
          <div className="compliance-section">
            <div 
              className="compliance-header"
              onClick={() => setShowComplianceSection(!showComplianceSection)}
              style={{ 
                cursor: 'pointer', 
                padding: '15px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '8px',
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #ddd'
              }}
            >
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                🔒 Data Compliance (Advanced)
                <button className="faq-trigger" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setActiveFaq('compliance'); }}>!</button>
              </h3>
              <span style={{ fontSize: '20px' }}>
                {showComplianceSection ? '▼' : '▶'}
              </span>
            </div>

            {showComplianceSection && (
              <div className="compliance-content" style={{ 
                padding: '20px', 
                border: '1px solid #ddd', 
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                backgroundColor: '#fafafa'
              }}>
                <div className="input-group">
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="enableComplianceControls"
                      checked={enableComplianceControls}
                      onChange={(e) => setEnableComplianceControls(e.target.checked)}
                      className="form-checkbox"
                    />
                    <label htmlFor="enableComplianceControls" className="checkbox-label">
                      <strong>Enable Compliance Controls</strong> (Master switch for all compliance features)
                    </label>
                  </div>
                </div>

                {enableComplianceControls && (
                  <>
                    <div style={{ marginTop: '20px', paddingLeft: '20px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '15px' }}>
                        Dropbox Bucket Compliance
                      </h4>
                      
                      <div className="input-group">
                        <div className="checkbox-group">
                          <input
                            type="checkbox"
                            id="enableDropboxObjectLock"
                            checked={enableDropboxObjectLock}
                            onChange={(e) => setEnableDropboxObjectLock(e.target.checked)}
                            className="form-checkbox"
                          />
                          <label htmlFor="enableDropboxObjectLock" className="checkbox-label">
                            Enable Dropbox Object Lock
                          </label>
                        </div>
                      </div>

                      {enableDropboxObjectLock && (
                        <>
                          <div className="input-group" style={{ marginLeft: '20px' }}>
                            <label className="form-label">Retention Mode</label>
                            <select
                              value={dropboxObjectLockRetentionMode}
                              onChange={(e) => setDropboxObjectLockRetentionMode(e.target.value)}
                              className="form-input"
                            >
                              <option value="GOVERNANCE">GOVERNANCE</option>
                              <option value="COMPLIANCE">COMPLIANCE</option>
                            </select>
                          </div>

                          <div className="input-group" style={{ marginLeft: '20px' }}>
                            <label className="form-label">
                              Retention Days (1-36500): {dropboxObjectLockRetentionDays}
                            </label>
                            <input
                              type="range"
                              min="1"
                              max="36500"
                              value={dropboxObjectLockRetentionDays}
                              onChange={(e) => setDropboxObjectLockRetentionDays(e.target.value)}
                              className="form-input"
                            />
                          </div>
                        </>
                      )}

                      <div className="input-group">
                        <div className="checkbox-group">
                          <input
                            type="checkbox"
                            id="enableDropboxChecksumValidation"
                            checked={enableDropboxChecksumValidation}
                            onChange={(e) => setEnableDropboxChecksumValidation(e.target.checked)}
                            className="form-checkbox"
                          />
                          <label htmlFor="enableDropboxChecksumValidation" className="checkbox-label">
                            Enable Dropbox Checksum Validation (SHA256)
                          </label>
                        </div>
                      </div>
                    </div>

                    <div style={{ marginTop: '20px', paddingLeft: '20px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '15px' }}>
                        Shared Folder Compliance
                      </h4>
                      
                      <div className="input-group">
                        <div className="checkbox-group">
                          <input
                            type="checkbox"
                            id="enableSharedFolderObjectLock"
                            checked={enableSharedFolderObjectLock}
                            onChange={(e) => setEnableSharedFolderObjectLock(e.target.checked)}
                            className="form-checkbox"
                          />
                          <label htmlFor="enableSharedFolderObjectLock" className="checkbox-label">
                            Enable Shared Folder Object Lock
                          </label>
                        </div>
                      </div>

                      {enableSharedFolderObjectLock && (
                        <>
                          <div className="input-group" style={{ marginLeft: '20px' }}>
                            <label className="form-label">Retention Mode</label>
                            <select
                              value={sharedFolderObjectLockRetentionMode}
                              onChange={(e) => setSharedFolderObjectLockRetentionMode(e.target.value)}
                              className="form-input"
                            >
                              <option value="GOVERNANCE">GOVERNANCE</option>
                              <option value="COMPLIANCE">COMPLIANCE</option>
                            </select>
                          </div>

                          <div className="input-group" style={{ marginLeft: '20px' }}>
                            <label className="form-label">
                              Retention Days (1-36500): {sharedFolderObjectLockRetentionDays}
                            </label>
                            <input
                              type="range"
                              min="1"
                              max="36500"
                              value={sharedFolderObjectLockRetentionDays}
                              onChange={(e) => setSharedFolderObjectLockRetentionDays(e.target.value)}
                              className="form-input"
                            />
                          </div>
                        </>
                      )}

                    </div>

                    <div style={{ marginTop: '20px', paddingLeft: '20px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '15px' }}>
                        User Data Compliance
                      </h4>
                      
                      <div className="input-group">
                        <div className="checkbox-group">
                          <input
                            type="checkbox"
                            id="enableUserDataObjectLock"
                            checked={enableUserDataObjectLock}
                            onChange={(e) => setEnableUserDataObjectLock(e.target.checked)}
                            className="form-checkbox"
                          />
                          <label htmlFor="enableUserDataObjectLock" className="checkbox-label">
                            Enable User Data Object Lock
                          </label>
                        </div>
                      </div>

                      {enableUserDataObjectLock && (
                        <>
                          <div className="input-group" style={{ marginLeft: '20px' }}>
                            <label className="form-label">Retention Mode</label>
                            <select
                              value={userDataObjectLockRetentionMode}
                              onChange={(e) => setUserDataObjectLockRetentionMode(e.target.value)}
                              className="form-input"
                            >
                              <option value="GOVERNANCE">GOVERNANCE</option>
                              <option value="COMPLIANCE">COMPLIANCE</option>
                            </select>
                          </div>

                          <div className="input-group" style={{ marginLeft: '20px' }}>
                            <label className="form-label">
                              Retention Days (1-36500): {userDataObjectLockRetentionDays}
                            </label>
                            <input
                              type="range"
                              min="1"
                              max="36500"
                              value={userDataObjectLockRetentionDays}
                              onChange={(e) => setUserDataObjectLockRetentionDays(e.target.value)}
                              className="form-input"
                            />
                          </div>
                        </>
                      )}
                    </div>

                    <div style={{ marginTop: '20px', paddingLeft: '20px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '15px' }}>
                        General S3 Compliance
                      </h4>
                      
                      <div className="input-group">
                        <div className="checkbox-group">
                          <input
                            type="checkbox"
                            id="enableS3AccessLogging"
                            checked={enableS3AccessLogging}
                            onChange={(e) => setEnableS3AccessLogging(e.target.checked)}
                            className="form-checkbox"
                          />
                          <label htmlFor="enableS3AccessLogging" className="checkbox-label">
                            Enable S3 Access Logging
                          </label>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="input-group">
            <div className="checkbox-group">
              <input
                type="checkbox"
                id="acceptedTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="form-checkbox"
                disabled={isAccountRequested}
              />
              <label htmlFor="acceptedTerms" className="checkbox-label">
                I have read and agree to the{" "}
                <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
                  <strong>Terms of Service</strong>
                </a>
              </label>
            </div>
          </div>

          {submitError && (
            <div
              className="error-message"
              role="alert"
              style={{ marginBottom: '12px', padding: '10px 14px', backgroundColor: '#fff0f0', border: '1px solid #f5c6cb', borderRadius: '6px', color: '#721c24' }}
            >
              {submitError}
            </div>
          )}

          <button
            type="submit"
            disabled={isButtonDisabled}
            className="submit-button"
          >
            {isAccountRequested ? (
              <>
                Creating Account... <span className="spinner">⏳</span>
              </>
            ) : (
              "Create Account"
            )}
          </button>
        </form>
        <FaqPanel
          isOpen={!!activeFaq}
          title={activeFaq ? FAQ_DATA[activeFaq].title : ""}
          items={activeFaq ? FAQ_DATA[activeFaq].items : []}
          onClose={(e) => {
            if (e) e.preventDefault();
            setActiveFaq(null);
          }}
        />
      </>
    )
  );
};

export default CreateAccountForm;
